import { GenericError, UnknownError } from '@cian/peperrors';

import { isChatSyncError, ChatSyncError } from '../../errors/ChatSyncError';
import {
  TWebChatsSyncRequest,
  TWebChatsSyncResponse,
  fetchWebChatsSync,
} from '../../repositories/chats/v2/web/chats/sync';
import { EStatusSyncChatSchema } from '../../types/chatList';
import { TContext } from '../../types/context';
import { ETelemetryEvents } from '../../types/telemetry';
import { mapChatV2VersionToV1 } from '../../utils/v2ToV1/mapChat';

export type TSyncChatsV2ParamsRequest = Omit<TWebChatsSyncRequest, 'status'> & {
  /** Раздел чатов **/
  status?: EStatusSyncChatSchema;
};

type TSyncChatsV2Params = {
  parameters: TSyncChatsV2ParamsRequest;
  cookies?: string;
};

const DOMAIN = 'critical.syncChats';
const DEFAULT_ERROR_MESSAGE = 'Неизвестная ошибка при синхронизации чатов';

export async function syncChatsV2(
  { logger, httpApi, telemetry }: TContext,
  { cookies, parameters }: TSyncChatsV2Params,
) {
  const { status } = parameters;

  const options: TWebChatsSyncRequest = {
    ...parameters,
    status: status || EStatusSyncChatSchema.Active,
  };

  const startTime = Date.now();

  telemetry.increment(ETelemetryEvents.ChatsSynchronizeInit);

  try {
    const { response, statusCode }: TWebChatsSyncResponse = await fetchWebChatsSync({
      httpApi,
      parameters: options,
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
      },
    });

    if (statusCode !== 200) {
      const error = response.errors?.[0];

      if (error && isChatSyncError(error)) {
        throw new ChatSyncError({
          domain: DOMAIN,
          message: error.message,
          details: {
            code: error.code,
          },
        });
      }

      throw new UnknownError({
        domain: DOMAIN,
        message: response.message || DEFAULT_ERROR_MESSAGE,
        details: {
          code: statusCode,
          errors: response.errors ? JSON.stringify(response.errors) : undefined,
        },
      });
    }

    telemetry.increment(ETelemetryEvents.ChatsSynchronizeSuccess);
    telemetry.timing(ETelemetryEvents.ChatsSynchronizeTime, Date.now() - startTime);

    return {
      chats: response.chats.map(mapChatV2VersionToV1),
      movedChatIds: response.movedChatIds || [],
    };
  } catch (error) {
    telemetry.increment(ETelemetryEvents.ChatsSynchronizeFail);

    let errorToLog = error;

    if (!GenericError.isLike(error)) {
      errorToLog = new UnknownError({
        domain: DOMAIN,
        message: DEFAULT_ERROR_MESSAGE,
        details: error,
      });
    }

    logger.error(errorToLog, {
      microserviceName: 'chats',
      pathApi: '/v2/web/chats/sync/',
      parameters: JSON.stringify(options),
    });

    return {
      chats: null,
      movedChatIds: null,
    };
  }
}
